import { reportError } from "../lib/report";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

const RELOAD_KEY = "svx_chunk_reload";

// isStaleBuild reports whether an error came from loading a code chunk that
// no longer exists, which happens when a tab opened before a deploy navigates
// to a screen it hasn't loaded yet.
function isStaleBuild(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk .* failed|Unable to preload CSS/i.test(msg);
}

// reloadForNewBuild reloads the page to pick up the new deploy, at most once
// every 30 seconds so a genuinely missing file can't cause a reload loop.
export function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: still reload; the 30-second guard is best effort.
  }
  window.location.reload();
  return true;
}

// errorText is what the user can copy to support: the error, the page, and
// the first stack frame (file:line:column, mappable with the source map).
function errorText(err: unknown): string {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  const frame = err instanceof Error ? (err.stack ?? "").split("\n").find((l) => /\/assets\/|\.js:\d+/.test(l)) : undefined;
  const at = frame ? ` · at ${frame.trim().replace(/^at\s+/, "").replace(/https?:\/\/[^/]+/, "")}` : "";
  return `${msg} · ${window.location.pathname}${at}`.slice(0, 400);
}

class Boundary extends Component<{ children: ReactNode; resetKey?: string }, { error: unknown }> {
  state = { error: null as unknown };

  // Navigating away from a crashed screen clears the error. Keying the
  // boundary by page instead would rebuild everything inside it on every
  // click (the whole app flickered and jumped to the top).
  componentDidUpdate(prev: { resetKey?: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error != null) this.setState({ error: null });
  }

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (isStaleBuild(error) && reloadForNewBuild()) return;
    console.error("Screen crashed", error, info.componentStack);
    if (!isStaleBuild(error)) reportError(error, "render");
  }

  render() {
    const { error } = this.state;
    if (error == null) return this.props.children;
    const stale = isStaleBuild(error);
    return (
      <section className="card crash">
        <h2>{stale ? "SVENEX was updated" : "This screen hit a problem"}</h2>
        <p className="muted">
          {stale ? "A new version is available. Reload to continue where you were." : "Something went wrong showing this page. Reloading usually fixes it; if it keeps happening, let us know what you clicked."}
        </p>
        <button onClick={() => window.location.reload()}>Reload</button>
        {!stale && <p className="muted small crash-detail">Details for support: {errorText(error)}</p>}
      </section>
    );
  }
}

// RootErrorBoundary wraps the whole app (outside the router), so even a
// crash in the session or router layer shows a message, not a white page.
export function RootErrorBoundary({ children }: { children: ReactNode }) {
  return <Boundary>{children}</Boundary>;
}

// ErrorBoundary catches crashes in a screen so the rest of the app keeps
// working. It resets when the user navigates to another page.
export function ErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
}
